import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";
import {
  MessageSquare,
  Keyboard,
  Moon,
  Music,
  HeartHandshake,
} from "lucide-react";
import { ActionCard, CategoryType } from "@/components/ui/action-card";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { getUserSettings } from "@/lib/userSettings";
import { resolveInputBehavior, SCAN_MODES } from "@/lib/inputMethods";
import { ICON_MAP } from "@/components/ui/LucideIconPicker";
import { loadNavigationBoard, NavigationItem, NavigationBoardData } from "@/lib/navigationBoardTypes";
import { useFeatureFilteredItems } from "@/hooks/useFeatureFilteredItems";
import { getNavigationColorClasses } from "@/components/communication/NavigationColorPicker";
import { FocusedKeyboardIcon } from "@/components/home/FocusedKeyboardIcon";
import { FocusedMusicIcon } from "@/components/home/FocusedMusicIcon";

interface MenuItem {
  key: string;
  labelKey: string;
  labelFallback: string;
  icon: LucideIcon;
  action: string;
  category: CategoryType;
}

// Fallback icon map for core items
const FALLBACK_ICONS: Record<string, LucideIcon> = {
  Moon, HeartHandshake, MessageSquare, Keyboard, Music,
};

// Convert navigation item to menu item
function toMenuItem(item: NavigationItem, lang: string): MenuItem {
  const IconComponent = ICON_MAP[item.iconName] || FALLBACK_ICONS[item.iconName] || Music;
  const labelByLang: Record<string, string> = {
    he: item.labelHe,
    en: item.labelEn,
    ru: item.labelRu || item.labelEn,
    ar: item.labelAr || item.labelEn,
  };
  return {
    key: item.key,
    labelKey: item.key,
    labelFallback: labelByLang[lang] || item.labelEn,
    icon: IconComponent,
    action: item.action,
    category: item.color as CategoryType,
  };
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Custom focused icon renderers for selective SVG fills
const FOCUSED_ICON_RENDERERS: Record<string, (props: { className?: string; style?: React.CSSProperties }) => React.ReactNode> = {
  keyboard: (props) => <FocusedKeyboardIcon {...props} focused />,
  entertainment: (props) => <FocusedMusicIcon {...props} focused />,
};

interface HomeGridProps {
  onSelect?: (action: string) => void;
  onHelp?: () => void; // Callback for help action
  onEntertainment?: () => void; // Callback for entertainment submenu
}

type LayoutMode = 'cross' | 'row';

/**
 * Determine the layout mode based on control settings and resolved scan mode.
 * - Row layout: AUTO_SCAN or STEP_SCAN (1–3 inputs) — linear scanning
 * - Cross layout: ADVANCED_STEP or SPATIAL (4+ inputs), or non-scanning methods
 */
function getLayoutMode(): LayoutMode {
  const settings = getUserSettings();
  const controlMethod = settings.controlMethod;

  // Determine input count based on control method
  let inputCount = 1;
  if (controlMethod === 'external_switch' || controlMethod === 'switch_control') {
    inputCount = settings.externalSwitchCount || 1;
  } else if (controlMethod === 'eye_control' || controlMethod === 'eyecontrol') {
    inputCount = settings.eyeControlGestures?.length || 1;
  } else if (controlMethod === 'mouth_gestures') {
    inputCount = settings.mouthGestures?.length || 1;
  }

  const behavior = resolveInputBehavior(controlMethod, inputCount);

  // Only scanning methods vary layout; non-scanning always use cross
  if (behavior.scanning === SCAN_MODES.NONE) {
    return 'cross';
  }

  // Linear scan modes → row layout (fewer scan cycles)
  if (behavior.scanning === SCAN_MODES.AUTO || behavior.scanning === SCAN_MODES.STEP) {
    return 'row';
  }

  // ADVANCED_STEP / SPATIAL → cross layout (needs 2D grid)
  return 'cross';
}

export function HomeGrid({
  onSelect,
  onHelp,
  onEntertainment
}: HomeGridProps) {
  const navigate = useNavigate();
  const { t, dir, lang } = useTranslation();
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(getLayoutMode);
  const [rawMenuItems, setRawMenuItems] = useState<MenuItem[]>([]);
  const [allMenuItems, setAllMenuItems] = useState<MenuItem[]>([]);
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const [boardData, setBoardData] = useState<NavigationBoardData | null>(null);

  // Load customized navigation items -- reload on mount and when returning from editor
  useEffect(() => {
    const loadItems = () => {
      const board = loadNavigationBoard("home");
      setBoardData(board);
      const sorted = [...board.items].sort((a, b) => a.order - b.order);
      const hidden = new Set(sorted.filter(item => item.visible === false).map(item => item.key));
      setHiddenKeys(hidden);
      setAllMenuItems(sorted.map(item => toMenuItem(item, lang)));
      setRawMenuItems(sorted.filter(item => item.visible !== false).map(item => toMenuItem(item, lang)));
    };
    loadItems();

    const handleFocus = () => loadItems();
    window.addEventListener('focus', handleFocus);
    window.addEventListener('navigationBoardUpdated', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('navigationBoardUpdated', handleFocus);
    };
  }, [lang]);

  // Filter items by feature flags
  const menuItems = useFeatureFilteredItems(rawMenuItems);

  // Listen for control method changes
  useEffect(() => {
    const handleControlChange = () => {
      setLayoutMode(getLayoutMode());
    };

    window.addEventListener('controlMethodChanged', handleControlChange);
    window.addEventListener('scanningConfigChanged', handleControlChange);
    window.addEventListener('userSettingsChanged', handleControlChange);

    // Check on mount
    setLayoutMode(getLayoutMode());

    return () => {
      window.removeEventListener('controlMethodChanged', handleControlChange);
      window.removeEventListener('scanningConfigChanged', handleControlChange);
      window.removeEventListener('userSettingsChanged', handleControlChange);
    };
  }, []);

  const handleClick = (action: string) => {
    // Handle special actions
    if (action === "help") {
      onHelp?.();
      return;
    }

    if (action === "/entertainment") {
      if (onEntertainment) {
        onEntertainment();
      } else {
        navigate("/entertainment"); // Navigate to entertainment page with all options
      }
      return;
    }

    if (onSelect) {
      onSelect(action);
    } else {
      navigate(action);
    }
  };

  const getLabel = (item: MenuItem) => {
    const translated = t(item.labelKey as any);
    return translated !== item.labelKey ? translated : item.labelFallback;
  };

  // Default colors per key -- only apply hardcoded CSS class when color is unchanged
  const DEFAULT_COLORS: Record<string, string> = {
    communication: 'green', keyboard: 'pink', entertainment: 'purple', rest: 'teal', help: 'help',
  };
  const HOME_CARD_CLASSES: Record<string, string> = {
    communication: 'home-card-communication',
    keyboard: 'home-card-keyboard',
    entertainment: 'home-card-entertainment',
    rest: 'home-card-rest',
    help: 'home-card-help',
  };
  // Get styling info: CSS class if default color, or nav color classes + hex if custom
  const getItemStyles = (item: MenuItem) => {
    const isDefault = DEFAULT_COLORS[item.key] && item.category === DEFAULT_COLORS[item.key];
    if (isDefault) {
      return { cssClass: HOME_CARD_CLASSES[item.key] || '', borderClass: '', iconColorClass: '', hex: '' };
    }
    const navColors = getNavigationColorClasses(item.category);
    return { cssClass: '', borderClass: navColors.border, iconColorClass: navColors.iconColor, hex: navColors.hex };
  };

  // Render cross/plus pattern layout (5 squares)
  const renderCrossLayout = () => {
    const isHidden = (key: string) => hiddenKeys.has(key);

    // Dynamic mapping by order -- no RTL swap needed since the grid container
    // already has dir={dir} which handles visual mirroring automatically.
    // order: 0=center, 1=left, 2=top, 3=right, 4=bottom
    const sorted = [...allMenuItems].sort((a, b) => {
      const oa = boardData?.items.find(i => i.key === a.key)?.order ?? 0;
      const ob = boardData?.items.find(i => i.key === b.key)?.order ?? 0;
      return oa - ob;
    });

    const positions = {
      center: sorted[0],
      left: sorted[1],
      top: sorted[2],
      right: sorted[3],
      bottom: sorted[4],
    };

    // If items not loaded yet, show nothing
    if (!positions.top && !positions.center && !positions.bottom) {
      return null;
    }

    return (
      <div
        className="grid grid-cols-3 grid-rows-3 gap-2"
        style={{
          aspectRatio: '1/1',
          height: 'min(100%, calc(100vw - 6rem))',
          maxHeight: 'calc(100vh - 7rem)',
          width: 'auto',
        }}
      >
        {/* Top row - Communication in center */}
        <div className="relative col-start-2 row-start-1 rounded-xl transition-all duration-200">
          <motion.div variants={itemVariants} className="h-full" {...(positions.top && !isHidden(positions.top.key) ? { 'data-scan-item': '' } : {})}>
            {positions.top && !isHidden(positions.top.key) ? (
              <ActionCard
                icon={positions.top.icon}
                label={getLabel(positions.top)}
                onClick={() => handleClick(positions.top!.action)}
                category={positions.top.category}
                iconFillOnFocus={positions.top.key !== 'keyboard' && positions.top.key !== 'entertainment'}
                focusedIconRender={FOCUSED_ICON_RENDERERS[positions.top.key]}
                iconColorClass={getItemStyles(positions.top).iconColorClass || undefined}
                borderHex={getItemStyles(positions.top).hex || undefined}
                size="large"
                designId={getItemStyles(positions.top).cssClass ? `home-card-${positions.top.key}` : undefined}
                className={cn("w-full h-full aspect-square", getItemStyles(positions.top).cssClass, getItemStyles(positions.top).borderClass)}
              />
            ) : <div className="w-full h-full" />}
          </motion.div>
        </div>

        {/* Middle row - Keyboard, Help (center), Rest */}
        <div className="relative col-start-1 col-span-3 row-start-2 grid grid-cols-3 gap-2 rounded-xl transition-all duration-200">
          <motion.div variants={itemVariants} className="h-full" {...(positions.left && !isHidden(positions.left.key) ? { 'data-scan-item': '' } : {})}>
            {positions.left && !isHidden(positions.left.key) ? (
              <ActionCard
                icon={positions.left.icon}
                label={getLabel(positions.left)}
                onClick={() => handleClick(positions.left!.action)}
                category={positions.left.category}
                iconFillOnFocus={positions.left.key !== 'keyboard' && positions.left.key !== 'entertainment'}
                focusedIconRender={FOCUSED_ICON_RENDERERS[positions.left.key]}
                iconColorClass={getItemStyles(positions.left).iconColorClass || undefined}
                borderHex={getItemStyles(positions.left).hex || undefined}
                size="large"
                designId={getItemStyles(positions.left).cssClass ? `home-card-${positions.left.key}` : undefined}
                className={cn("w-full h-full aspect-square", getItemStyles(positions.left).cssClass, getItemStyles(positions.left).borderClass)}
              />
            ) : <div className="w-full h-full" />}
          </motion.div>

          <motion.div variants={itemVariants} className="h-full" {...(positions.center && !isHidden(positions.center.key) ? { 'data-scan-item': '', 'data-scan-default': '' } : {})}>
            {positions.center && !isHidden(positions.center.key) ? (
              <ActionCard
                icon={positions.center.icon}
                label={getLabel(positions.center)}
                onClick={() => handleClick(positions.center!.action)}
                category={positions.center.category}
                iconFillOnFocus={positions.center.key !== 'keyboard' && positions.center.key !== 'entertainment'}
                focusedIconRender={FOCUSED_ICON_RENDERERS[positions.center.key]}
                iconColorClass={getItemStyles(positions.center).iconColorClass || undefined}
                borderHex={getItemStyles(positions.center).hex || undefined}
                size="large"
                designId={getItemStyles(positions.center).cssClass ? `home-card-${positions.center.key}` : undefined}
                className={cn("w-full h-full aspect-square", getItemStyles(positions.center).cssClass, getItemStyles(positions.center).borderClass)}
              />
            ) : <div className="w-full h-full" />}
          </motion.div>

          <motion.div variants={itemVariants} className="h-full" {...(positions.right && !isHidden(positions.right.key) ? { 'data-scan-item': '' } : {})}>
            {positions.right && !isHidden(positions.right.key) ? (
              <ActionCard
                icon={positions.right.icon}
                label={getLabel(positions.right)}
                onClick={() => handleClick(positions.right!.action)}
                category={positions.right.category}
                iconFillOnFocus={positions.right.key !== 'keyboard' && positions.right.key !== 'entertainment'}
                focusedIconRender={FOCUSED_ICON_RENDERERS[positions.right.key]}
                iconColorClass={getItemStyles(positions.right).iconColorClass || undefined}
                borderHex={getItemStyles(positions.right).hex || undefined}
                size="large"
                designId={getItemStyles(positions.right).cssClass ? `home-card-${positions.right.key}` : undefined}
                className={cn("w-full h-full aspect-square", getItemStyles(positions.right).cssClass, getItemStyles(positions.right).borderClass)}
              />
            ) : <div className="w-full h-full" />}
          </motion.div>
        </div>

        {/* Bottom row - Entertainment in center */}
        <div className="relative col-start-2 row-start-3 rounded-xl transition-all duration-200">
          <motion.div variants={itemVariants} className="h-full" {...(positions.bottom && !isHidden(positions.bottom.key) ? { 'data-scan-item': '' } : {})}>
            {positions.bottom && !isHidden(positions.bottom.key) ? (
              <ActionCard
                icon={positions.bottom.icon}
                label={getLabel(positions.bottom)}
                onClick={() => handleClick(positions.bottom!.action)}
                category={positions.bottom.category}
                iconFillOnFocus={positions.bottom.key !== 'keyboard' && positions.bottom.key !== 'entertainment'}
                focusedIconRender={FOCUSED_ICON_RENDERERS[positions.bottom.key]}
                iconColorClass={getItemStyles(positions.bottom).iconColorClass || undefined}
                borderHex={getItemStyles(positions.bottom).hex || undefined}
                size="large"
                designId={getItemStyles(positions.bottom).cssClass ? `home-card-${positions.bottom.key}` : undefined}
                className={cn("w-full h-full aspect-square", getItemStyles(positions.bottom).cssClass, getItemStyles(positions.bottom).borderClass)}
              />
            ) : <div className="w-full h-full" />}
          </motion.div>
        </div>
      </div>
    );
  };

  // Render row layout (5 items in single row) - always 5 slots for consistent layout
  const renderRowLayout = () => {
    // Order: Rest, Help, Communication, Keyboard, Entertainment
    const getItemByKey = (key: string) => menuItems.find(i => i.key === key);
    const orderedItems = [
      getItemByKey('rest'),
      getItemByKey('help'),
      getItemByKey('communication'),
      getItemByKey('keyboard'),
      getItemByKey('entertainment'),
    ];

    // Compute a uniform square side: the lesser of (available_width / 5) and available_height.
    // This guarantees equal width and height regardless of viewport size.
    // (100vw - 6rem) accounts for sidebar; divided by 5 items; (100vh - 12rem) for header/footer.
    const itemSize = 'min(calc((100vw - 6rem) / 5 - 1rem), calc(100vh - 12rem))';

    return (
      <div className="flex flex-row items-center justify-center gap-4 w-full relative rounded-2xl transition-all duration-200">
        {Array.from({ length: 5 }).map((_, index) => {
          const item = orderedItems[index];

          // Empty slot if no item at this index (maintains grid structure)
          if (!item) {
            return (
              <div
                key={`empty-${index}`}
                style={{ width: itemSize, height: itemSize, flexShrink: 0 }}
              />
            );
          }

          return (
            <motion.div
              key={item.key}
              variants={itemVariants}
              data-scan-item
              style={{ width: itemSize, height: itemSize, flexShrink: 0 }}
            >
              <ActionCard
                icon={item.icon}
                label={getLabel(item)}
                onClick={() => handleClick(item.action)}
                category={item.category}
                iconFillOnFocus={item.key !== 'keyboard' && item.key !== 'entertainment'}
                focusedIconRender={FOCUSED_ICON_RENDERERS[item.key]}
                iconColorClass={getItemStyles(item).iconColorClass || undefined}
                borderHex={getItemStyles(item).hex || undefined}
                size="large"
                designId={getItemStyles(item).cssClass ? `home-card-${item.key}` : undefined}
                className={cn("w-full h-full", getItemStyles(item).cssClass, getItemStyles(item).borderClass)}
              />
            </motion.div>
          );
        })}
      </div>
    );
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col items-center justify-center gap-2 w-full h-full max-h-full min-h-0"
      dir={dir}
    >
      {layoutMode === 'cross' ? renderCrossLayout() : renderRowLayout()}
    </motion.div>
  );
}
